import { PermissionSlug } from '../constants/featureFlags';

type ClinicPermissionAssignment = {
  clinic_id: string;
  permissions?: readonly string[];
};

type AssignedClinician = {
  id: string;
  user_id: string;
};

export function getAssignedClinicianUserId(
  assignedClinicianId: string | null | undefined,
  clinicians: AssignedClinician[],
  persistedAssignedClinicianUserId?: string | null
): string | null | undefined {
  if (!assignedClinicianId) return null;
  if (persistedAssignedClinicianUserId !== undefined) {
    return persistedAssignedClinicianUserId ?? undefined;
  }
  return clinicians.find(clinician => clinician.id === assignedClinicianId)
    ?.user_id;
}

function hasActiveClinicMembership(
  activeClinicIds: readonly string[] | undefined,
  clinicId: string | null | undefined
): boolean {
  return Boolean(clinicId && activeClinicIds?.includes(clinicId));
}

function hasClinicRoleEntry(
  clinicPermissions: readonly ClinicPermissionAssignment[] | undefined,
  clinicId: string | null | undefined
): boolean {
  return Boolean(
    clinicId && clinicPermissions?.some(clinic => clinic.clinic_id === clinicId)
  );
}

function hasClinicPermission(
  clinicPermissions: readonly ClinicPermissionAssignment[] | undefined,
  clinicId: string | null | undefined,
  permission: string
): boolean {
  return Boolean(
    clinicId &&
    clinicPermissions?.some(
      clinic =>
        clinic.clinic_id === clinicId &&
        clinic.permissions?.includes(permission)
    )
  );
}

type ChartEditPermissionArgs = {
  assignedClinicianUserId: string | null | undefined;
  currentUserId: string | undefined;
  clinicId: string | null | undefined;
  clinicPermissions: readonly ClinicPermissionAssignment[] | undefined;
  activeClinicIds?: readonly string[];
  agencyPermissions?: readonly string[];
};

export type EpisodeEditorCapabilities = {
  canEditDates: boolean;
  canEditStatus: boolean;
  canEditPreClaimReview: boolean;
  canEditNotes: boolean;
};

/**
 * Assignment is the ordinary chart-edit boundary. An unassigned chart needs
 * the dedicated clinic or agency permission; missing clinic data stays
 * read-only because the permission scope cannot be established.
 */
export function canEditChartContent({
  assignedClinicianUserId,
  currentUserId,
  clinicId,
  clinicPermissions,
  activeClinicIds,
  agencyPermissions,
}: ChartEditPermissionArgs): boolean {
  if (assignedClinicianUserId === undefined || currentUserId === undefined) {
    return false;
  }
  if (!hasActiveClinicMembership(activeClinicIds, clinicId)) return false;
  if (assignedClinicianUserId === currentUserId) return true;
  if (assignedClinicianUserId !== null) {
    return hasClinicPermission(
      clinicPermissions,
      clinicId,
      PermissionSlug.CHART_EDIT_UNASSIGNED
    );
  }
  return (
    (agencyPermissions?.includes(
      PermissionSlug.CHART_EDIT_UNASSIGNED_AGENCY
    ) === true &&
      hasClinicRoleEntry(clinicPermissions, clinicId)) ||
    hasClinicPermission(
      clinicPermissions,
      clinicId,
      PermissionSlug.CHART_EDIT_UNASSIGNED
    )
  );
}

function canEditEpisodeManager({
  assignedClinicianUserId,
  currentUserId,
  clinicId,
  clinicPermissions,
  activeClinicIds,
  agencyPermissions,
  allowAgencyPermission,
}: ChartEditPermissionArgs & { allowAgencyPermission: boolean }): boolean {
  if (assignedClinicianUserId === undefined || currentUserId === undefined) {
    return false;
  }
  if (!hasActiveClinicMembership(activeClinicIds, clinicId)) return false;
  if (assignedClinicianUserId === currentUserId) return true;
  if (
    allowAgencyPermission &&
    agencyPermissions?.includes(
      PermissionSlug.EPISODE_MANAGER_EDIT_UNASSIGNED_AGENCY
    ) === true
  ) {
    return true;
  }
  return hasClinicPermission(
    clinicPermissions,
    clinicId,
    PermissionSlug.EPISODE_MANAGER_EDIT_UNASSIGNED
  );
}

/** Episode Manager fields retain the existing agency/clinic behavior by default. */
export function canEditEpisodeManagerFields(
  args: ChartEditPermissionArgs,
  options: { allowAgencyPermission?: boolean } = {}
): boolean {
  return canEditEpisodeManager({
    ...args,
    allowAgencyPermission: options.allowAgencyPermission ?? true,
  });
}

/**
 * The agency-wide Episode Manager grant opens only the date fields. Existing
 * owners and clinic-scoped Episode Managers keep the editor controls they had.
 */
export function getEpisodeEditorCapabilities(
  args: ChartEditPermissionArgs
): EpisodeEditorCapabilities | null {
  if (!canEditEpisodeManagerFields(args)) {
    return null;
  }
  const hasExistingEditorAccess = canEditEpisodeManagerFields(args, {
    allowAgencyPermission: false,
  });
  return {
    canEditDates: true,
    canEditStatus: hasExistingEditorAccess,
    canEditPreClaimReview: hasExistingEditorAccess,
    canEditNotes: hasExistingEditorAccess,
  };
}

/** TasksTab scheduling controls accept the agency-wide Scheduling grant. */
export function canEditEpisodeManagerTask(
  args: ChartEditPermissionArgs
): boolean {
  return canEditEpisodeManager({ ...args, allowAgencyPermission: true });
}

export type { ClinicPermissionAssignment };
